// External triggers (feature spec 16 9.6; spec 11 6.3.11): the kinds registry and the document helpers. The property is
// found only through the type's `externalTriggersProperty`, never by its name.
import { Cable, Plug, Rabbit, Waypoints, Zap, type LucideIcon } from 'lucide-react';
import type { BlueprintDocument, BlueprintResource, JsonObject, ResourceType } from '../api/types';
import { strings } from '../strings';
import { injectionMapName } from './settings';

export type SuggestionSource = 'plain' | 'parameter' | 'secret';

export interface SuggestedSetting {
  name: string;
  source: SuggestionSource;
  description: string;
}

export interface ExternalTriggerKindDefinition {
  kind: string;
  label: string;
  icon: LucideIcon;
  suggestedSettings(name: string): SuggestedSetting[];
  /** Undefined: no example for that runtime. */
  codeExample?(name: string, runtime: string): string | undefined;
  /** False for the generic fallback (label = kind, Zap icon, no suggestions). */
  known: boolean;
}

const e = strings.externalTriggers;
const sg = e.suggestion;

const KINDS: readonly ExternalTriggerKindDefinition[] = [
  {
    kind: 'kafka',
    label: e.kinds.kafka,
    icon: Waypoints,
    known: true,
    suggestedSettings: (n) => [
      { name: `${n}_BROKER_LIST`, source: 'parameter', description: sg.brokerList },
      { name: `${n}_TOPIC`, source: 'plain', description: sg.topic },
      { name: `${n}_CONSUMER_GROUP`, source: 'plain', description: sg.consumerGroup },
      { name: `${n}_USERNAME`, source: 'plain', description: sg.username },
      { name: `${n}_PASSWORD`, source: 'secret', description: sg.password },
    ],
    codeExample: (n, runtime) =>
      runtime === 'dotnet-isolated'
        ? `[KafkaTrigger("%${n}_BROKER_LIST%", "%${n}_TOPIC%", ConsumerGroup = "%${n}_CONSUMER_GROUP%", Username = "%${n}_USERNAME%", Password = "%${n}_PASSWORD%", Protocol = BrokerProtocol.SaslSsl, AuthenticationMode = BrokerAuthenticationMode.Plain)]`
        : undefined,
  },
  {
    kind: 'rabbitmq',
    label: e.kinds.rabbitmq,
    icon: Rabbit,
    known: true,
    suggestedSettings: (n) => [
      { name: `${n}_CONNECTION`, source: 'secret', description: sg.rabbitConnection },
      { name: `${n}_QUEUE`, source: 'plain', description: sg.rabbitQueue },
    ],
    codeExample: (n, runtime) =>
      runtime === 'dotnet-isolated'
        ? `[RabbitMQTrigger("%${n}_QUEUE%", ConnectionStringSetting = "${n}_CONNECTION")]`
        : undefined,
  },
  {
    kind: 'connector',
    label: e.kinds.connector,
    icon: Plug,
    known: true,
    suggestedSettings: (n) => [
      { name: `${n}_CONNECTION_RUNTIME_URL`, source: 'parameter', description: sg.runtimeUrl },
    ],
  },
  { kind: 'other', label: e.kinds.other, icon: Cable, known: true, suggestedSettings: () => [] },
];

/** The registry entry of a kind; unknown kinds get the generic entry. */
export function externalTriggerKind(kind: string): ExternalTriggerKindDefinition {
  return (
    KINDS.find((k) => k.kind === kind) ?? {
      kind,
      label: kind,
      icon: Zap,
      known: false,
      suggestedSettings: () => [],
    }
  );
}

/** Every registry entry (the Source types dialog and tests). */
export const externalTriggerKinds: readonly ExternalTriggerKindDefinition[] = KINDS;

export interface ExternalTriggerEntry {
  name: string;
  kind: string;
  settings: string[];
  description?: string;
}

/** The top-level property named by the type's `externalTriggersProperty`, or null. */
export function externalTriggersPropertyName(type: ResourceType | undefined): string | null {
  const pointer = type?.externalTriggersProperty;
  if (!pointer || !pointer.startsWith('/') || pointer.indexOf('/', 1) >= 0) return null;
  return pointer.slice(1).replaceAll('~1', '/').replaceAll('~0', '~');
}

/** The kinds the property's schema allows (`items.properties.kind.enum`). */
export function externalTriggerKindsOf(type: ResourceType | undefined): string[] {
  const name = externalTriggersPropertyName(type);
  const properties = type?.propertiesSchema.properties as Record<string, Record<string, unknown>> | undefined;
  const property = name ? properties?.[name] : undefined;
  const items = property?.items as { properties?: Record<string, { enum?: unknown[] }> } | undefined;
  const values = items?.properties?.kind?.enum;
  return Array.isArray(values) ? values.filter((v): v is string => typeof v === 'string') : [];
}

/** The resource's external triggers (malformed entries are read as far as they go). */
export function readExternalTriggers(
  resource: BlueprintResource,
  type: ResourceType | undefined,
): ExternalTriggerEntry[] {
  const name = externalTriggersPropertyName(type);
  const value = name ? resource.properties?.[name] : undefined;
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const entry = (item && typeof item === 'object' && !Array.isArray(item) ? item : {}) as Record<
      string,
      unknown
    >;
    return {
      name: typeof entry.name === 'string' ? entry.name : '',
      kind: typeof entry.kind === 'string' ? entry.kind : '',
      settings: Array.isArray(entry.settings)
        ? entry.settings.filter((s): s is string => typeof s === 'string')
        : [],
      ...(typeof entry.description === 'string' ? { description: entry.description } : {}),
    };
  });
}

/** The first external trigger that lists a setting (Variables rows, spec 6.10.4). */
export function externalTriggerOf(
  resource: BlueprintResource,
  type: ResourceType | undefined,
  key: string,
): { name: string; kind: string } | null {
  const entry = readExternalTriggers(resource, type).find((t) => t.settings.includes(key));
  return entry ? { name: entry.name, kind: entry.kind } : null;
}

function settingsOf(resource: BlueprintResource, mapName: string): Record<string, unknown> {
  const map = resource.properties?.[mapName];
  return map && typeof map === 'object' && !Array.isArray(map) ? (map as Record<string, unknown>) : {};
}

function entryJson(entry: ExternalTriggerEntry): JsonObject {
  return {
    name: entry.name,
    kind: entry.kind,
    ...(entry.description ? { description: entry.description } : {}),
    settings: entry.settings,
  };
}

function withResource(
  doc: BlueprintDocument,
  resourceId: string,
  update: (resource: BlueprintResource) => BlueprintResource,
): BlueprintDocument {
  return { ...doc, resources: doc.resources.map((r) => (r.id === resourceId ? update(r) : r)) };
}

/** What Add or Edit applies, as one document change (one undo step). */
export interface ExternalTriggerChange {
  /** Replaced entry (Edit); null adds a new one. */
  index: number | null;
  entry: ExternalTriggerEntry;
  /** New settings with their values (`${param.x}`, `${secret.y}` or a literal). */
  newSettings: { name: string; value: string }[];
  newParameters: string[];
  newSecrets: string[];
}

export function applyExternalTrigger(
  doc: BlueprintDocument,
  resourceId: string,
  type: ResourceType | undefined,
  change: ExternalTriggerChange,
): BlueprintDocument {
  const property = externalTriggersPropertyName(type);
  const mapName = injectionMapName(type);
  if (!property || !mapName) return doc;
  let next: BlueprintDocument = doc;
  if (change.newParameters.length > 0) {
    const parameters = { ...doc.parameters };
    for (const name of change.newParameters) parameters[name] ??= { type: 'string' };
    next = { ...next, parameters };
  }
  if (change.newSecrets.length > 0) {
    const secrets = { ...doc.secrets };
    for (const name of change.newSecrets) secrets[name] ??= { onChange: 'wait' };
    next = { ...next, secrets };
  }
  return withResource(next, resourceId, (resource) => {
    const settings: JsonObject = { ...(settingsOf(resource, mapName) as JsonObject) };
    for (const s of change.newSettings) if (!(s.name in settings)) settings[s.name] = s.value;
    const entries = readExternalTriggers(resource, type).map(entryJson);
    if (change.index === null) entries.push(entryJson(change.entry));
    else entries[change.index] = entryJson(change.entry);
    return { ...resource, properties: { ...resource.properties, [mapName]: settings, [property]: entries } };
  });
}

/** Settings an entry lists that no other entry lists (the ones Remove may also remove). */
export function removableSettings(
  entries: readonly ExternalTriggerEntry[],
  index: number,
): { own: string[]; shared: string[] } {
  const others = new Set(entries.filter((_, i) => i !== index).flatMap((t) => t.settings));
  const listed = entries[index]?.settings ?? [];
  return { own: listed.filter((s) => !others.has(s)), shared: listed.filter((s) => others.has(s)) };
}

export function removeExternalTrigger(
  doc: BlueprintDocument,
  resourceId: string,
  type: ResourceType | undefined,
  index: number,
  alsoSettings: boolean,
): BlueprintDocument {
  const property = externalTriggersPropertyName(type);
  const mapName = injectionMapName(type);
  if (!property) return doc;
  return withResource(doc, resourceId, (resource) => {
    const entries = readExternalTriggers(resource, type);
    const properties: JsonObject = { ...resource.properties };
    const remaining = entries.filter((_, i) => i !== index).map(entryJson);
    if (remaining.length > 0) properties[property] = remaining;
    else delete properties[property];
    if (alsoSettings && mapName) {
      const settings: JsonObject = { ...(settingsOf(resource, mapName) as JsonObject) };
      for (const name of removableSettings(entries, index).own) delete settings[name];
      properties[mapName] = settings;
    }
    return { ...resource, properties };
  });
}

/** Adds a listed setting that is missing, with an empty plain value (the card's Add setting). */
export function addMissingSetting(
  doc: BlueprintDocument,
  resourceId: string,
  type: ResourceType | undefined,
  name: string,
): BlueprintDocument {
  const mapName = injectionMapName(type);
  if (!mapName) return doc;
  return withResource(doc, resourceId, (resource) => ({
    ...resource,
    properties: {
      ...resource.properties,
      [mapName]: { ...(settingsOf(resource, mapName) as JsonObject), [name]: '' },
    },
  }));
}

/** A free name: `name`, else `name<sep>2`, `name<sep>3`, … (spec 6.3.11 collisions). */
export function freeName(name: string, taken: Iterable<string>, separator: string): string {
  const set = new Set([...taken].map((t) => t.toLowerCase()));
  if (!set.has(name.toLowerCase())) return name;
  let n = 2;
  while (set.has(`${name}${separator}${n}`.toLowerCase())) n++;
  return `${name}${separator}${n}`;
}

/** `Payments_BROKER_LIST` → `paymentsBrokerList` (a parameter name). */
export function parameterNameFor(setting: string): string {
  const parts = setting.split('_').filter(Boolean);
  const name = parts
    .map((p, i) =>
      i === 0
        ? p.charAt(0).toLowerCase() + p.slice(1).toLowerCase()
        : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    )
    .join('');
  return /^[A-Za-z]/.test(name) ? name.slice(0, 63) : `p${name}`.slice(0, 63);
}

/** `Payments_PASSWORD` → `payments-password` (a secret declaration name). */
export function secretNameForSetting(setting: string): string {
  const name = setting.toLowerCase().replaceAll('_', '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return (name || 'secret').slice(0, 63);
}

export const EXTERNAL_TRIGGER_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/** DOM id of a card's focus target (problem navigation, spec 6.3.7). */
export function externalTriggerFocusId(
  resourceId: string,
  index: number,
  part: 'card' | 'edit' | 'remove' | `setting-${number}`,
) {
  return `ext-${resourceId}-${index}-${part}`;
}
